// The simple structure types: Reactor (power), Warehouse (credit storage + the credits
// share on takeover/death), Radar (power-gated anim + first-radar announce), HRC (the
// infantry builder). game/Reactor.cpp + Warehouse.cpp + Radar.cpp + HRC.cpp +
// CalcCreditsShare (game/Processor.cpp:76-84, shared by Warehouse/Processor).
// Sfx ids are host-bound; the file-static gConsts become ctx.unitConsts entries.

import type { SimContext } from '../context.ts';
import { StructGob } from './struct.ts';
import { kfGobActive } from './gob.ts';
import { MobileUnitBuilderGob } from './builder.ts';
import {
  StructConsts,
  MobileUnitBuilderConsts,
  InitStructConstsClass,
  InitBuilderConstsClass,
  kfUntcHasFullnessIndicator,
  kfUntcNotifyPowerLowHigh,
  kfUntcMobileUnitBuilder,
} from './unitconsts.ts';
import { kgtReactor, kgtWarehouse, kgtRadar, kgtHumanResourceCenter } from './gobtypes.ts';
import {
  kutReactor,
  kutWarehouse,
  kutRadar,
  kutHumanResourceCenter,
  kutProcessor,
} from '../player/side.ts';
import { kumReactor, kumProcessor, kumInfantry, kumAndy, kumFox } from '../player/masks.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  newMessage,
  knHandled,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedUpdate,
  kmidPlaySfx,
  kmidBuildComplete,
  kfUpgradeHrc,
  kfUpgradeHrcInProgress,
  kstIdle,
  kstDying,
  kstBeingBuilt,
} from '../sm/message.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxReactorAbortRepair,
  ksfxReactorRepair,
  ksfxReactorDamaged,
  ksfxReactorSelect,
  ksfxReactorDestroyed,
  ksfxGalaxiteWarehouseAbortRepair,
  ksfxGalaxiteWarehouseRepair,
  ksfxGalaxiteWarehouseDamaged,
  ksfxGalaxiteWarehouseSelect,
  ksfxGalaxiteWarehouseDestroyed,
  ksfxRadarAbortRepair,
  ksfxRadarRepair,
  ksfxRadarDamaged,
  ksfxRadarSelect,
  ksfxRadarDestroyed,
  ksfxHumanResourceCenterAbortRecruiting,
  ksfxHumanResourceCenterRecruit,
  ksfxHumanResourceCenterUnitReady,
  ksfxHumanResourceCenterAbortRepair,
  ksfxHumanResourceCenterDamaged,
  ksfxHumanResourceCenterDestroyed,
  ksfxHumanResourceCenterRepair,
  ksfxHumanResourceCenterSelect,
} from '../data/soundeffects.ts';

// game/Processor.cpp:76-84 — the credits this store 'owns'
export function CalcCreditsShare(pplr: Player): number {
  const cWarehouses = pplr.GetUnitCount(kutWarehouse);
  const cProcessors = pplr.GetUnitCount(kutProcessor);
  return (pplr.GetCredits() / (cWarehouses + cProcessors)) | 0;
}

// game/Reactor.cpp:15-33
export function InitReactorClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new StructConsts().bind(ctx);
  c.gt = kgtReactor;
  c.ut = kutReactor;
  // Sound effects (Reactor.cpp:22-27)
  c.sfxAbortRepair = ksfxReactorAbortRepair;
  c.sfxRepair = ksfxReactorRepair;
  c.sfxDamaged = ksfxReactorDamaged;
  c.sfxSelect = ksfxReactorSelect;
  c.sfxDestroyed = ksfxReactorDestroyed;
  c.sfxImpact = ksfxNothing;
  return InitStructConstsClass(ctx, c, pini);
}

export class ReactorGob extends StructGob {
  constructor(ctx: SimContext) {
    // Reactor.cpp:36-38
    super(ctx, ctx.unitConsts[kutReactor] as StructConsts);
  }
}

// game/Warehouse.cpp:15-32
export function InitWarehouseClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new StructConsts().bind(ctx);
  c.gt = kgtWarehouse;
  c.ut = kutWarehouse;
  c.umPrerequisites = (kumReactor | kumProcessor) >>> 0;
  c.wf |= kfUntcHasFullnessIndicator;
  // Sound effects (Warehouse.cpp:24-29)
  c.sfxAbortRepair = ksfxGalaxiteWarehouseAbortRepair;
  c.sfxRepair = ksfxGalaxiteWarehouseRepair;
  c.sfxDamaged = ksfxGalaxiteWarehouseDamaged;
  c.sfxSelect = ksfxGalaxiteWarehouseSelect;
  c.sfxDestroyed = ksfxGalaxiteWarehouseDestroyed;
  c.sfxImpact = ksfxNothing;
  return InitStructConstsClass(ctx, c, pini);
}

export class WarehouseGob extends StructGob {
  constructor(ctx: SimContext) {
    // Warehouse.cpp:38-40
    super(ctx, ctx.unitConsts[kutWarehouse] as StructConsts);
  }

  // game/Warehouse.cpp:44-56 — the new owner takes this Warehouse's credit share
  override Takeover(pplr: Player): void {
    const cCreditsTaken = CalcCreditsShare(this.m_pplr as Player);
    (this.m_pplr as Player).SetCredits((this.m_pplr as Player).GetCredits() - cCreditsTaken, true);
    pplr.SetCredits(pplr.GetCredits() + cCreditsTaken, true);

    super.Takeover(pplr);
  }

  // game/Warehouse.cpp:106-145 — idle/dying never animate through the fullness frames;
  // dying loses the player's share of credits BEFORE the StructGob dying enter
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstIdle) {
      if (pmsg.mid === kmidReservedUpdate) {
        this.DefUpdate();
        return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstDying) {
      if (pmsg.mid === kmidReservedEnter) {
        if (this.m_ff & kfGobActive) {
          (this.m_pplr as Player).SetCredits(
            (this.m_pplr as Player).GetCredits() - CalcCreditsShare(this.m_pplr as Player),
            true,
          );
        }
        return super.ProcessStateMachineMessage(st, pmsg);
      }
      if (pmsg.mid === kmidReservedUpdate) {
        return knHandled; // no animating through the level frames
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// game/Radar.cpp:15-36
export function InitRadarClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new StructConsts().bind(ctx);
  c.gt = kgtRadar;
  c.ut = kutRadar;
  c.umPrerequisites = kumReactor;
  c.wf |= kfUntcNotifyPowerLowHigh;
  // Sound effects (Radar.cpp:23-28)
  c.sfxAbortRepair = ksfxRadarAbortRepair;
  c.sfxRepair = ksfxRadarRepair;
  c.sfxDamaged = ksfxRadarDamaged;
  c.sfxSelect = ksfxRadarSelect;
  c.sfxDestroyed = ksfxRadarDestroyed;
  c.sfxImpact = ksfxNothing;
  return InitStructConstsClass(ctx, c, pini);
}

export class RadarGob extends StructGob {
  constructor(ctx: SimContext) {
    // Radar.cpp:43-45
    super(ctx, ctx.unitConsts[kutRadar] as StructConsts);
  }

  // game/Radar.cpp:49-54 — the minimap radar recalc is host UI (local player only)
  override Activate(): void {
    super.Activate();
    if (this.ctx.plrLocal === this.m_pplr && (this.m_pplr as Player).GetUnitCount(kutRadar) === 1) {
      this.ctx.host.CalcPoweredRadar();
    }
  }

  // game/Radar.cpp:56-61
  override Deactivate(): void {
    super.Deactivate();
    if (this.ctx.plrLocal === this.m_pplr && (this.m_pplr as Player).GetUnitCount(kutRadar) === 0) {
      this.ctx.host.CalcPoweredRadar();
    }
  }

  // game/Radar.cpp:73-112
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      if (pmsg.mid === kmidPlaySfx) {
        if (this.ctx.plrLocal === this.m_pplr) {
          this.ctx.host.PlaySfx(pmsg.PlaySfx.sfx);
        }
        return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstBeingBuilt) {
      if (pmsg.mid === kmidBuildComplete) {
        // Announce new structure options shortly, if this is the first radar.
        // The DELAYED MESSAGE is sim state (it routes through the tracked smm) even
        // though its effect (PlaySfx) is host-only.
        if ((this.m_pplr as Player).GetUnitCount(kutRadar) === 0) {
          const msgT = newMessage(kmidPlaySfx, this.m_gid, this.m_gid);
          msgT.PlaySfx.sfx = 24; // ksfxGameNewStructureOptions (soundeffects.h positional enum) — SERIALIZED in the delayed-message image
          this.ctx.smm!.SendDelayedMsg(msgT, 96);
        }
        return super.ProcessStateMachineMessage(st, pmsg);
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstIdle) {
      if (pmsg.mid === kmidReservedUpdate) {
        // No animation while power is low (kmidPowerLowHigh wakes us on change)
        if (!(this.m_pplr as Player).IsPowerLow()) {
          this.AdvanceAnimation(this.m_ani);
        }
        this.DefUpdate();
        return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// game/HRC.cpp:19-56
export function InitHrcClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitBuilderConsts().bind(ctx);
  c.gt = kgtHumanResourceCenter;
  c.ut = kutHumanResourceCenter;
  c.umPrerequisites = kumReactor;
  c.umCanBuild = (kumInfantry & ~(kumAndy | kumFox)) >>> 0;
  c.wf |= kfUntcMobileUnitBuilder;
  c.fUpgrade = kfUpgradeHrc;
  c.fUpgradeInProgress = kfUpgradeHrcInProgress;
  // Sound effects (HRC.cpp:39-46)
  c.sfxUnitBuildAbort = ksfxHumanResourceCenterAbortRecruiting;
  c.sfxUnitBuild = ksfxHumanResourceCenterRecruit;
  c.sfxUnitReady = ksfxHumanResourceCenterUnitReady;
  c.sfxAbortRepair = ksfxHumanResourceCenterAbortRepair;
  c.sfxDamaged = ksfxHumanResourceCenterDamaged;
  c.sfxDestroyed = ksfxHumanResourceCenterDestroyed;
  c.sfxRepair = ksfxHumanResourceCenterRepair;
  c.sfxSelect = ksfxHumanResourceCenterSelect;
  return InitBuilderConstsClass(ctx, c, pini);
}

export class HrcGob extends MobileUnitBuilderGob {
  constructor(ctx: SimContext) {
    // HRC.cpp:64-66
    super(ctx, ctx.unitConsts[kutHumanResourceCenter] as MobileUnitBuilderConsts);
  }
}

// self-register (see factory.ts)
import { registerGobType } from './factory.ts';
registerGobType(kgtReactor, (ctx) => new ReactorGob(ctx));
registerGobType(kgtWarehouse, (ctx) => new WarehouseGob(ctx));
registerGobType(kgtRadar, (ctx) => new RadarGob(ctx));
registerGobType(kgtHumanResourceCenter, (ctx) => new HrcGob(ctx));
